import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getStoredToken } from '../../utils/tokenStorage';

interface DuplicateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  fileName: string;
}

interface Project {
  id: number;
  name: string;
}

const DuplicateDialog: React.FC<DuplicateDialogProps> = ({ isOpen, onClose, fileId, fileName }) => {
  const { branding } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [targetProjectId, setTargetProjectId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const token = getStoredToken();
    fetch('/api/projects/', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.projects || [];
        setProjects(list);
        if (list.length > 0) setTargetProjectId(list[0].id);
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDuplicate = async () => {
    if (!targetProjectId) return;
    setSubmitting(true);
    setError('');

    try {
      const token = getStoredToken();
      const res = await fetch(`/api/v1/files/${fileId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_project_id: targetProjectId }),
      });

      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || 'Failed to duplicate');
        setSubmitting(false);
        return;
      }

      const data = await res.json();
      onClose();
      navigate(`/chemistry/file/${data.new_file_id}/editor`);
    } catch {
      setError('Failed to duplicate file');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-sm p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-4">Duplicate to My Project</h3>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm text-slate-400 mb-1">Duplicate to:</label>
          <select
            value={targetProjectId ?? ''}
            onChange={(e) => setTargetProjectId(parseInt(e.target.value))}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-teal-500 outline-none"
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDuplicate}
            disabled={submitting || !targetProjectId}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
            style={{
              background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
            }}
          >
            {submitting ? 'Duplicating...' : 'Duplicate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateDialog;
